"use client";

import { useState } from "react";
import { Checkbox } from "@/components/tool-kit/primitives";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { toBubble } from "@/lib/tool-kit/text/bubble";

function ResultCard({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) {
  const strings = useToolUi(BUBBLE_TEXT_GENERATOR_STRINGS);
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    if (value.length === 0) {
      return;
    }
    writeToClipboard(value).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-foreground text-sm">{label}</p>
        <button
          className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
          disabled={value.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copy}
        </button>
      </div>
      <p className="mt-2 min-h-6 whitespace-pre-wrap break-words text-foreground text-sm leading-relaxed">
        {value.length > 0 ? (
          value
        ) : (
          <span className="text-muted-foreground">
            {strings.typeAboveToSeeOutput}
          </span>
        )}
      </p>
      {note && <p className="mt-2 text-muted-foreground text-xs">{note}</p>}
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const BUBBLE_TEXT_GENERATOR_STRINGS = {
  inputText: "Input Text",
  typeOrPasteYourText: "Type or paste your text here...",
  results: "Results",
  bubbleText: "Bubble text (ⓐⒶ①)",
  filledBubbleText: "Filled bubble text (🅐❶)",
  aNoteOnAccessibility: "A note on accessibility",
  theseAreSpecialUnicodeCharacters:
    "These are special Unicode characters, not fonts or formatting. Screen readers may read them out letter by letter, mispronounce them, or skip them entirely, and they can break text search and spell checking. Avoid them in accessible or professional documents.",
  copied: "Copied!",
  copy: "Copy",
  typeAboveToSeeOutput: "Type above to see output…",
  noFilledLowercaseNote:
    "Unicode has no filled lowercase circles, so lowercase letters become filled capitals.",
  keepDigitsSeparate: "Circle every digit separately (10 becomes ①⓪, not ⑩)",
  outlinedNumbersNote:
    "Two-digit numbers from 10 to 50 become one circle (⑩ ㉑ ㊿). Longer numbers, such as 2026, and numbers from 51 up are circled digit by digit.",
  filledNumbersNote:
    "Filled single circles exist only for 10 to 20 (❿ ⓫–⓴); other numbers are circled digit by digit.",
} as const;

export default function BubbleTextGeneratorWidget() {
  const [input, setInput] = useState("");
  const [keepDigits, setKeepDigits] = useState(false);

  const strings = useToolUi(BUBBLE_TEXT_GENERATOR_STRINGS);

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="bubble-input"
        >
          {strings.inputText}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="bubble-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.typeOrPasteYourText}
          value={input}
        />
      </div>
      <Checkbox
        checked={keepDigits}
        label={strings.keepDigitsSeparate}
        onChange={setKeepDigits}
      />
      {/* Results */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.results}
        </h2>
        <div className="space-y-3">
          <ResultCard
            label={strings.bubbleText}
            note={keepDigits ? undefined : strings.outlinedNumbersNote}
            value={toBubble(input, "outlined", { keepDigits })}
          />
          <ResultCard
            label={strings.filledBubbleText}
            note={
              keepDigits
                ? strings.noFilledLowercaseNote
                : `${strings.noFilledLowercaseNote} ${strings.filledNumbersNote}`
            }
            value={toBubble(input, "filled", { keepDigits })}
          />
        </div>
      </section>
      {/* Caveat */}
      <section className="rounded-lg border border-border bg-muted/30 p-5">
        <h2 className="font-semibold text-base text-foreground">
          {strings.aNoteOnAccessibility}
        </h2>
        <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
          {strings.theseAreSpecialUnicodeCharacters}
        </p>
      </section>
    </>
  );
}
